// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp } from "@insite/mobius/utilities";
import { xBaseTheme } from "@synd/XDL/types";
import { autoClamp } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export const fontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap",
    fontFamily: "Poppins, Arial, sans-serif",
    fontFamilyUrl: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap",
    secondaryFontFamily: "TT Ramillas, Arial, sans-serif",
    weight: {
        light: 300,
        regular: 400,
        medium: 500,
        semiBold: 600,
        bold: 700,
        black: 900,
    },
    xxSmall: {
        size: 10,
        lineHeight: "16px",
    },
    xxSmallSingleLine: {
        size: 10,
        lineHeight: "15px",
    },
    xSmall: {
        size: 12,
        lineHeight: "18px",
    },
    xSmallSingleLine: {
        size: 12,
        lineHeight: "17px",
    },
    xxxSmallSingleLine: {
        size: 8,
        lineHeight: "11px",
    },
    small: {
        size: 14,
        lineHeight: "22px",
    },
    smallSingleLine: {
        size: 14,
        lineHeight: "20px",
    },
    regular: {
        size: 16,
        lineHeight: "24px",
    },
    regularSingleLine: {
        size: 16,
        lineHeight: "24px",
    },
    smallHeadLine: {
        size: 18,
        lineHeight: "26px",
    },
    headLine: {
        size: 42,
        lineHeight: "59px",
    },
    headLineMobile: {
        size: 36,
        lineHeight: "50px",
    },
};

export default {
    fontFamilyImportUrl: fontConfig.url,
    body: {
        size: "15px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "15px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 15px;
            letter-spacing: 0;
            line-height: 23px;
            display: block;
        `,
    },

    h1: {
        fontFamily: fontConfig.secondaryFontFamily,
        size: autoClamp(32, 35, 38),
        weight: fontConfig.weight.medium,
        lineHeight: autoClamp(53, 50, 44),
        css: css`
            margin-bottom: 24px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h2Sans: {
        size: autoClamp(28, 30, 32),
        weight: fontConfig.weight.semiBold,
        lineHeight: autoClamp(41, 43, 45),
        color: "common.brand04",
        as: "h2",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h2: {
        fontFamily: fontConfig.secondaryFontFamily,
        size: autoClamp(28, 30, 32),
        weight: fontConfig.weight.black,
        lineHeight: autoClamp(39, 42, 44),
        color: "common.brand04",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h3: {
        size: autoClamp(22, 22, 24),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(33, 34, 35),
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h4: {
        size: "18px",
        weight: fontConfig.weight.bold,
        lineHeight: "26px",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h5: {
        size: "16px",
        weight: fontConfig.weight.semiBold,
        lineHeight: "23px",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h6: {
        size: "14px",
        weight: fontConfig.weight.semiBold,
        lineHeight: "20px",
        transform: "uppercase",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.bold,
        color: "text",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.black,
        fontFamily: fontConfig.fontFamily,
        color: "text",
    },
    headerTertiary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.medium,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text",
        transform: "uppercase",
        weight: fontConfig.weight.medium,
    },
} as xBaseTheme["typography"];
